import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { locationsApi, employeesApi, usersApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  Users,
  MapPin,
  Clock,
  UserCog,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';

const Locations = () => {
  const { user, loadLocations: refreshLocations } = useAuth();
  const [locations, setLocations] = useState([]);
  const [formerLocations, setFormerLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [newLocation, setNewLocation] = useState({ name: '', address: '', phone: '', timezone: 'America/New_York' });

  const isAdmin = user?.role === 'admin' || user?.role === 'accounting_admin';

  const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern (ET)' },
    { value: 'America/Chicago', label: 'Central (CT)' },
    { value: 'America/Denver', label: 'Mountain (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
    { value: 'America/Anchorage', label: 'Alaska (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
    { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [locs, emps, users, former] = await Promise.all([
        locationsApi.getAll(),
        employeesApi.getAll(),
        isAdmin ? usersApi.getAll() : Promise.resolve([]),
        isAdmin ? locationsApi.getFormer().catch(() => []) : Promise.resolve([]),
      ]);
      setLocations(locs);
      setEmployees(emps);
      setManagers(users.filter(u => u.role === 'manager'));
      setFormerLocations(former);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreLocation = async (id) => {
    try {
      await locationsApi.restore(id);
      await loadData();
      await refreshLocations();
    } catch (error) {
      alert('Failed to restore location: ' + error.message);
    }
  };

  const handleAddLocation = async () => {
    try {
      await locationsApi.create(newLocation);
      await loadData();
      await refreshLocations();
      setShowAddModal(false);
      setNewLocation({ name: '', address: '', phone: '', timezone: 'America/New_York' });
    } catch (error) {
      alert('Failed to add location: ' + error.message);
    }
  };

  const handleUpdateLocation = async () => {
    if (!editingLocation) return;
    try {
      await locationsApi.update(editingLocation.id, {
        name: editingLocation.name,
        address: editingLocation.address,
        phone: editingLocation.phone || '',
        timezone: editingLocation.timezone,
      });
      await loadData();
      await refreshLocations();
      setEditingLocation(null);
    } catch (error) {
      alert('Failed to update location: ' + error.message);
    }
  };

  const handleDeleteLocation = async (id) => {
    try {
      await locationsApi.delete(id);
      await loadData();
      await refreshLocations();
      setShowDeleteConfirm(null);
    } catch (error) {
      alert('Failed to delete location: ' + error.message);
    }
  };

  const getLocationStats = (locationId) => {
    const empCount = employees.filter(e => e.location_id === locationId).length;
    const manager = managers.find(m => m.managed_location === locationId);
    return { empCount, manager };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Locations</h1>
          <p className="text-gray-500 mt-1">Manage your business locations</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowAddModal(true)} className="bg-purple-600 hover:bg-purple-700" data-testid="add-location-btn">
            <Plus className="w-4 h-4 mr-2" />Add Location
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Locations</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{locations.length}</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-xl"><Building2 className="w-6 h-6 text-white" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Employees</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{employees.length}</p>
              </div>
              <div className="bg-purple-500 p-3 rounded-xl"><Users className="w-6 h-6 text-white" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active Managers</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{managers.length}</p>
              </div>
              <div className="bg-purple-500 p-3 rounded-xl"><UserCog className="w-6 h-6 text-white" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      {isAdmin && (
        <div className="mb-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="active" data-testid="active-locations-tab">
                <Building2 className="w-4 h-4 mr-2" />Active ({locations.length})
              </TabsTrigger>
              <TabsTrigger value="former" data-testid="former-locations-tab">
                <Trash2 className="w-4 h-4 mr-2" />Former ({formerLocations.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      )}

      {/* Locations Table */}
      {activeTab === 'active' ? (
      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Location</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Manager</TableHead>
                <TableHead>Employees</TableHead>
                <TableHead>Timezone</TableHead>
                {isAdmin && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map((loc) => {
                const stats = getLocationStats(loc.id);
                return (
                  <TableRow key={loc.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{loc.name}</p>
                          <span className="text-xs font-mono text-gray-400">{loc.id}{loc.employee_prefix ? ` · ${loc.employee_prefix}-xxx` : ''}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        {loc.address}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{loc.phone || <span className="text-gray-400">-</span>}</TableCell>
                    <TableCell>
                      {stats.manager ? (
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center">
                            <span className="text-purple-600 text-xs font-medium">
                              {stats.manager.name?.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <span className="text-sm text-gray-600">{stats.manager.name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">No manager assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-blue-100 text-blue-700">
                        <Users className="w-3 h-3 mr-1" />{stats.empCount} employees
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-gray-600">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{loc.timezone}</span>
                      </div>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setEditingLocation(loc)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(loc)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          {locations.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No locations found</p>
            </div>
          )}
        </CardContent>
      </Card>
      ) : (
      <Card className="border-0 shadow-md" data-testid="former-locations-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Location</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Deleted</TableHead>
                <TableHead>Deleted By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {formerLocations.map((loc) => (
                <TableRow key={loc.id} className="hover:bg-gray-50" data-testid={`former-location-row-${loc.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{loc.name}</p>
                        <span className="text-xs font-mono text-gray-400">{loc.id}{loc.employee_prefix ? ` · ${loc.employee_prefix}-xxx` : ''}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600">{loc.address}</TableCell>
                  <TableCell className="text-sm text-gray-500">{loc.deleted_at ? new Date(loc.deleted_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell className="text-sm text-gray-500">{loc.deleted_by_name || '-'}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-700 border-green-200 hover:bg-green-50"
                      onClick={() => handleRestoreLocation(loc.id)}
                      data-testid={`restore-location-${loc.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />Restore
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {formerLocations.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Trash2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No former locations</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Add Location Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add New Location</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={newLocation.name} onChange={(e) => setNewLocation({...newLocation, name: e.target.value})} placeholder="e.g., Downtown Cafe" /></div>
            <div><Label>Address</Label><Input value={newLocation.address} onChange={(e) => setNewLocation({...newLocation, address: e.target.value})} placeholder="e.g., 123 Main St, City, ST 12345" /></div>
            <div><Label>Phone</Label><Input value={newLocation.phone} onChange={(e) => setNewLocation({...newLocation, phone: e.target.value})} placeholder="e.g., (555) 123-4567" data-testid="new-location-phone" /></div>
            <div><Label>Timezone</Label>
              <Select value={newLocation.timezone} onValueChange={(v) => setNewLocation({...newLocation, timezone: v})}>
                <SelectTrigger data-testid="new-location-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleAddLocation} className="bg-purple-600 hover:bg-purple-700" disabled={!newLocation.name || !newLocation.address}>Add Location</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Location Modal */}
      <Dialog open={!!editingLocation} onOpenChange={() => setEditingLocation(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Location</DialogTitle></DialogHeader>
          {editingLocation && (
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={editingLocation.name} onChange={(e) => setEditingLocation({...editingLocation, name: e.target.value})} /></div>
              <div><Label>Address</Label><Input value={editingLocation.address} onChange={(e) => setEditingLocation({...editingLocation, address: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={editingLocation.phone || ''} onChange={(e) => setEditingLocation({...editingLocation, phone: e.target.value})} placeholder="e.g., (555) 123-4567" data-testid="edit-location-phone" /></div>
              <div><Label>Timezone</Label>
                <Select value={editingLocation.timezone} onValueChange={(v) => setEditingLocation({...editingLocation, timezone: v})}>
                  <SelectTrigger data-testid="edit-location-timezone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLocation(null)}>Cancel</Button>
            <Button onClick={handleUpdateLocation} className="bg-purple-600 hover:bg-purple-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Location</DialogTitle></DialogHeader>
          <p>Are you sure you want to delete &quot;{showDeleteConfirm?.name}&quot;? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDeleteLocation(showDeleteConfirm?.id)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Locations;
